import { getProgressBubbleTop } from './get-progress-bubble-top'

/**
 * How fast the fill bar's head travels during the post-jump catch-up sprint.
 * The duration scales with the jump's pixel distance at this speed, clamped
 * below — timing eyeballed from the Figma prototype's compressed demo loop,
 * pending final motion params (#261).
 */
export const CATCHUP_SPEED_PX_PER_S = 310
export const CATCHUP_MIN_SPRINT_S = 0.6
export const CATCHUP_MAX_SPRINT_S = 4

interface ScoreAnchor {
  /** Normalized sprint progress in [0, 1]. */
  p: number
  /** The rolling number's value when the sprint reaches `p`. */
  score: number
}

export interface CatchupTimeline {
  /** Head top (track px) at the old, pre-jump score's segment midpoint. */
  fromTop: number
  /** Head top (track px) at the new score's segment midpoint. */
  toTop: number
  /** Sprint duration in seconds; scales with pixel distance, clamped. */
  durationS: number
  /**
   * Piecewise-linear progress→score mapping. The head's top moves linearly
   * from `fromTop` to `toTop`, so anchoring each crossed checkpoint's score
   * at the progress where the head touches its node keeps the rolling
   * number, the node done-flips, and the reward flips in visual lockstep.
   */
  scoreAnchors: ScoreAnchor[]
}

export interface CatchupDisplay {
  /** Continuous head top (track px) — overrides the midpoint step function. */
  top: number
  /** The rolling number (unrounded). */
  score: number
}

/**
 * The pure model of the post-jump catch-up tween: where the fill bar's head
 * starts and rests (segment-midpoint semantics on both ends), how long the
 * sprint runs, and how the rolling number maps onto the head's travel.
 *
 * Returns null when there is no forward distance to animate — the caller
 * renders the refetched state directly.
 */
export function getCatchupTimeline(input: {
  fromScore: number
  toScore: number
  checkpoints: number[]
  step: number
}): CatchupTimeline | null {
  const { fromScore, toScore, checkpoints, step } = input
  if (toScore <= fromScore) return null

  const fromTop = getProgressBubbleTop(fromScore, checkpoints, step)
  const toTop = getProgressBubbleTop(toScore, checkpoints, step)
  const distance = toTop - fromTop

  const scoreAnchors: ScoreAnchor[] = [{ p: 0, score: fromScore }]
  // A same-segment jump has zero distance: the head parks while the number
  // rolls, and no checkpoint node is crossed.
  if (distance > 0) {
    checkpoints.forEach((checkpoint, index) => {
      if (checkpoint <= fromScore || checkpoint > toScore) return
      const p = (index * step - fromTop) / distance
      // p ≥ 1 only when the destination clamps onto the last node — the
      // final anchor already covers that position.
      if (p < 1) scoreAnchors.push({ p, score: checkpoint })
    })
  }
  scoreAnchors.push({ p: 1, score: toScore })

  const durationS = Math.min(
    Math.max(CATCHUP_MIN_SPRINT_S, distance / CATCHUP_SPEED_PX_PER_S),
    CATCHUP_MAX_SPRINT_S,
  )

  return { fromTop, toTop, durationS, scoreAnchors }
}

/** Head top and rolling number at a normalized sprint progress. */
export function getCatchupDisplay(
  timeline: CatchupTimeline,
  progress: number,
): CatchupDisplay {
  const { fromTop, toTop, scoreAnchors } = timeline
  const p = Math.min(Math.max(progress, 0), 1)
  const top = fromTop + (toTop - fromTop) * p

  let before = scoreAnchors[0]
  let after = scoreAnchors[scoreAnchors.length - 1]
  for (const anchor of scoreAnchors) {
    if (anchor.p <= p) before = anchor
    if (anchor.p >= p) {
      after = anchor
      break
    }
  }
  const span = after.p - before.p
  const score =
    span === 0
      ? after.score
      : before.score + ((after.score - before.score) * (p - before.p)) / span

  return { top, score }
}
